import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // withCredentials = true: trình duyệt tự gửi httpOnly cookie theo mỗi request
  // Token không còn lưu trong localStorage nữa — an toàn hơn trước XSS
  withCredentials: true,
});

// Admin: view-as-user. Set this to a userId string to send X-As-User-Id on every request.
let _adminViewUserId = null;
export const setAdminViewUserId = (userId) => { _adminViewUserId = userId; };

// Request interceptor — inject X-As-User-Id when admin is viewing another user
api.interceptors.request.use((config) => {
  if (_adminViewUserId) {
    config.headers['X-As-User-Id'] = _adminViewUserId;
  }
  return config;
});

// Callback từ AuthContext — reset state khi token hết hạn
let _onUnauthorized = null;
export const setOnUnauthorized = (handler) => { _onUnauthorized = handler; };

let _handlingUnauthorized = false;

const isAuthPublicRequest = (url = '') =>
  url.includes('/auth/login') || url.includes('/auth/register');

// Response interceptor — tự động logout khi token hết hạn (401)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const requestUrl = error.config?.url || '';
    if (isAuthPublicRequest(requestUrl)) {
      return Promise.reject(error);
    }

    const path = window.location.pathname;
    if (path === '/login' || path === '/register') {
      return Promise.reject(error);
    }

    if (_handlingUnauthorized) {
      return Promise.reject(error);
    }

    _handlingUnauthorized = true;
    try {
      await api.post('/auth/logout');
    } catch {
      // Cookie có thể đã hết hạn — vẫn tiếp tục logout phía client
    }
    _onUnauthorized?.();
    sessionStorage.setItem('session_expired', '1');
    window.location.href = '/login';
    return Promise.reject(error);
  }
);

export default api;

// API Functions
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data)
};

export const khoiAPI = {
  getAll: () => api.get('/khoi'),
  getById: (id) => api.get(`/khoi/${id}`),
  create: (data) => api.post('/khoi', data),
  update: (id, data) => api.put(`/khoi/${id}`, data),
  reorder: (khoiIds) => api.put('/khoi/order', { khoiIds }),
  delete: (id) => api.delete(`/khoi/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/khoi/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const lopHocAPI = {
  getAll: () => api.get('/lop-hoc'),
  getById: (id) => api.get(`/lop-hoc/${id}`),
  create: (data) => api.post('/lop-hoc', data),
  update: (id, data) => api.put(`/lop-hoc/${id}`, data),
  updateOrder: (id, thuTu) => api.put(`/lop-hoc/${id}/order`, { thuTu }),
  reorder: (orderedIds) => api.put('/lop-hoc/order', { orderedIds }),
  delete: (id) => api.delete(`/lop-hoc/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/lop-hoc/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const monHocAPI = {
  getAll: () => api.get('/mon-hoc'),
  getById: (id) => api.get(`/mon-hoc/${id}`),
  create: (data) => api.post('/mon-hoc', data),
  update: (id, data) => api.put(`/mon-hoc/${id}`, data),
  updateOrder: (id, thuTu) => api.put(`/mon-hoc/${id}/order`, { thuTu }),
  reorder: (orderedIds) => api.put('/mon-hoc/order', { orderedIds }),
  delete: (id) => api.delete(`/mon-hoc/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/mon-hoc/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const giaoVienAPI = {
  getAll: () => api.get('/giao-vien'),
  getById: (id) => api.get(`/giao-vien/${id}`),
  create: (data) => api.post('/giao-vien', data),
  update: (id, data) => api.put(`/giao-vien/${id}`, data),
  updateOrder: (id, thuTu) => api.put(`/giao-vien/${id}/order`, { thuTu }),
  reorder: (orderedIds) => api.put('/giao-vien/order', { orderedIds }),
  delete: (id) => api.delete(`/giao-vien/${id}`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/giao-vien/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const phanCongAPI = {
  getAll: () => api.get('/phan-cong'),
  getById: (id) => api.get(`/phan-cong/${id}`),
  getByGiaoVien: (gvId) => api.get(`/phan-cong/giao-vien/${gvId}`),
  getByLopHoc: (lopId) => api.get(`/phan-cong/lop-hoc/${lopId}`),
  create: (data) => api.post('/phan-cong', data),
  update: (id, data) => api.put(`/phan-cong/${id}`, data),
  delete: (id) => api.delete(`/phan-cong/${id}`),
  deleteAll: () => api.delete('/phan-cong/all/clear'),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/phan-cong/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const tkbAPI = {
  getAll: () => api.get('/tkb'),
  getById: (id) => api.get(`/tkb/${id}`),
  getByLopHoc: (lopId) => api.get(`/tkb/lop-hoc/${lopId}`),
  getByGiaoVien: (gvId) => api.get(`/tkb/giao-vien/${gvId}`),
  getByThu: (thu) => api.get(`/tkb/thu/${thu}`),
  getMatrix: (lopId) => api.get('/tkb/matrix', { params: { lopId } }),
  create: (data) => api.post('/tkb', data),
  update: (id, data) => api.put(`/tkb/${id}`, data),
  delete: (id) => api.delete(`/tkb/${id}`),
  deleteByLop: (lopId) => api.delete(`/tkb/lop-hoc/${lopId}`),
  deleteAll: () => api.delete('/tkb/all/clear'),
  placeSinhHoat: () => api.post('/tkb/place-sinh-hoat'),
  spreadUnits: (blockedCells, fixedCells) => api.post('/tkb/spread-units', { blockedCells, fixedCells }),
  scheduleSessionV4: (thu, buoi, blockedCells, fixedCells) => api.post('/tkb/schedule-session-v4', { thu, buoi, blockedCells, fixedCells }),
  scheduleSessionBacktrack: (thu, buoi, blockedCells, fixedCells) => api.post('/tkb/schedule-session-backtrack', { thu, buoi, blockedCells, fixedCells }),
  adjustTeacherSessions: (fixedCells) => api.post('/tkb/adjust-sessions', { fixedCells }),
  scheduleOverflow: (unassignedUnits, blockedCells, fixedCells) => api.post('/tkb/schedule-overflow', { unassignedUnits, blockedCells, fixedCells })
};

export const nguoiDungAPI = {
  getProfile: () => api.get('/nguoi-dung/profile'),
  updateProfile: (data) => api.put('/nguoi-dung/profile', data),
  deleteAccount: () => api.delete('/nguoi-dung/account'),
  getDashboard: () => api.get('/nguoi-dung/dashboard'),
  getAllUsers: () => api.get('/nguoi-dung/admin/users')
};

export const blockedCellAPI = {
  getAll: () => api.get('/blocked-cells'),
  create: (data) => api.post('/blocked-cells', data),
  createBatch: (cells) => api.post('/blocked-cells/batch', { cells }),
  delete: (id) => api.delete(`/blocked-cells/${id}`),
  deleteAll: () => api.delete('/blocked-cells')
};

export const fixedCellAPI = {
  getAll: () => api.get('/fixed-cells'),
  create: (data) => api.post('/fixed-cells', data),
  deleteByCell: (lop_id, thu, buoi, tiet) => api.post('/fixed-cells/delete-by-cell', { lop_id: lop_id === 'all' ? null : lop_id, thu, buoi, tiet }),
  delete: (id) => api.delete(`/fixed-cells/${id}`),
  deleteAll: () => api.delete('/fixed-cells')
};

export const gvConstraintAPI = {
  getAll: () => api.get('/gv-constraints'),
  getByGiaoVien: (gvId) => api.get(`/gv-constraints/${gvId}`),
  saveForGiaoVien: (gvId, constraints, force = false) =>
    api.put(`/gv-constraints/${gvId}`, { constraints, force }),
  validateAll: () => api.post('/gv-constraints/validate')
};
